import type { ParentEntityType } from '@bower/shared';
import { ApiResponseError } from '../../../../services/api/http-client.types';
import type { Destination } from './destination';

/** Where the create modal saves: the workspace root or a collection, never a note. */
export type CreateDestination = Exclude<Destination, { type: 'note' }>;

export function workspaceDestination(workspace: {
  readonly id: string;
  readonly name: string;
}): CreateDestination {
  return { type: 'workspace', id: workspace.id, name: workspace.name };
}

export function collectionDestination(collection: {
  readonly id: string;
  readonly name: string;
}): CreateDestination {
  return { type: 'project', id: collection.id, name: collection.name };
}

/** The collection id a create sends, or `undefined` for the workspace root. */
export function destinationProjectId(destination: CreateDestination): string | undefined {
  return destination.type === 'project' ? destination.id : undefined;
}

/** The parent type the registry filters on (`createActionsForParent`). */
export function destinationParentType(destination: CreateDestination): ParentEntityType {
  return destination.type === 'project' ? 'project' : 'workspace';
}

/** The parent a create sends: the root or a collection. */
export function destinationParent(destination: CreateDestination): {
  readonly parentEntityType: 'workspace' | 'project';
  readonly parentEntityId: string;
} {
  return { parentEntityType: destination.type, parentEntityId: destination.id };
}

/**
 * Why a create was refused because of where it was going, or `null` when the
 * failure had nothing to do with the destination. The endpoints disagree on
 * codes: a trashed or invisible parent is `INVALID_PARENT` (400) from artifact
 * creates and upload reserve, `PARENT_PROJECT_NOT_FOUND` (404) from collection
 * create, `NOT_FOUND` from a blank spreadsheet; lost edit access is
 * `ENTITY_ACCESS_DENIED` (or reserve's `VIEWER_FORBIDDEN`, 403).
 */
export function destinationProblem(error: unknown): 'gone' | 'forbidden' | null {
  if (!(error instanceof ApiResponseError)) return null;
  switch (error.code) {
    case 'INVALID_PARENT':
    case 'PARENT_PROJECT_NOT_FOUND':
    case 'NOT_FOUND':
      return 'gone';
    case 'ENTITY_ACCESS_DENIED':
    case 'VIEWER_FORBIDDEN':
      return 'forbidden';
    default:
      return null;
  }
}

export function destinationProblemMessage(
  problem: 'gone' | 'forbidden',
  destination: CreateDestination,
): string {
  return problem === 'gone'
    ? `${destination.name} was deleted or moved to Trash. Choose another place.`
    : `You can't add to ${destination.name}. Choose another place.`;
}
